import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Snackbar,
  TextField,
  CircularProgress,
  Chip,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
} from '@mui/material';
import {
  RefreshOutlined,
  KeyOutlined,
  TuneOutlined,
  ModelTrainingOutlined,
  CheckCircleOutlined,
  ErrorOutlineOutlined,
  PendingActionsOutlined,
  SaveOutlined,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import apiService, { AssistantConfig, AssistantUsageSummary } from '../services/api';

const AssistantConfigPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [config, setConfig] = useState<AssistantConfig | null>(null);
  const [loading, setLoading] = useState<'load' | 'save-key' | 'save-limits' | 'model' | null>('load');
  const [openAiKey, setOpenAiKey] = useState('');
  const [caps, setCaps] = useState({
    token_limit_input: '',
    token_limit_output: '',
    weekly_token_limit_input: '',
    weekly_token_limit_output: '',
    monthly_token_limit_input: '',
    monthly_token_limit_output: '',
  });
  const [nextModel, setNextModel] = useState('');
  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  const [modelNote, setModelNote] = useState('');
  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' | 'info' }>({
    open: false, msg: '', severity: 'success',
  });

  const isSuperAdmin = user?.role === 'super_admin';

  const showSnack = (msg: string, severity: 'success' | 'error' | 'info' = 'success') =>
    setSnack({ open: true, msg, severity });

  const load = useCallback(async () => {
    setLoading('load');
    try {
      const c = await apiService.getAssistantConfig();
      setConfig(c);
      setCaps({
        token_limit_input: c.token_limit_input ? String(c.token_limit_input) : '',
        token_limit_output: c.token_limit_output ? String(c.token_limit_output) : '',
        weekly_token_limit_input: c.weekly_token_limit_input ? String(c.weekly_token_limit_input) : '',
        weekly_token_limit_output: c.weekly_token_limit_output ? String(c.weekly_token_limit_output) : '',
        monthly_token_limit_input: c.monthly_token_limit_input ? String(c.monthly_token_limit_input) : '',
        monthly_token_limit_output: c.monthly_token_limit_output ? String(c.monthly_token_limit_output) : '',
      });
      setNextModel(c.model ?? '');
    } catch (e: any) {
      showSnack(e.response?.data?.detail || 'Failed to load config', 'error');
    } finally {
      setLoading(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveKey = async () => {
    if (!openAiKey.trim()) {
      showSnack('Enter a key first', 'error');
      return;
    }
    setLoading('save-key');
    try {
      const c = await apiService.updateAssistantConfig({ openai_api_key: openAiKey.trim() });
      setConfig(c);
      setOpenAiKey('');
      showSnack('OpenAI key updated');
    } catch (e: any) {
      showSnack(e.response?.data?.detail || 'Failed to save key', 'error');
    } finally {
      setLoading(null);
    }
  };

  const saveLimits = async () => {
    setLoading('save-limits');
    try {
      const patch: any = {};
      const parseInto = (field: keyof typeof caps, allowZero: boolean) => {
        const raw = caps[field];
        if (raw === '') {
          if (allowZero) patch[field] = 0;
          return;
        }
        const n = parseInt(raw, 10);
        if (Number.isFinite(n) && n >= 0) patch[field] = n;
      };
      parseInto('token_limit_input', false);
      parseInto('token_limit_output', false);
      parseInto('weekly_token_limit_input', true);
      parseInto('weekly_token_limit_output', true);
      parseInto('monthly_token_limit_input', true);
      parseInto('monthly_token_limit_output', true);
      const c = await apiService.updateAssistantConfig(patch);
      setConfig(c);
      showSnack('Limits updated');
    } catch (e: any) {
      showSnack(e.response?.data?.detail || 'Failed to save limits', 'error');
    } finally {
      setLoading(null);
    }
  };

  const submitModelChange = async () => {
    if (!nextModel.trim()) {
      showSnack('Enter a model id', 'error');
      return;
    }
    setLoading('model');
    try {
      const res = await apiService.initiateOrConfirmAssistantModelChange(nextModel.trim(), modelNote.trim() || undefined);
      showSnack(res.message || 'Model change submitted');
      setModelDialogOpen(false);
      setModelNote('');
      await load();
    } catch (e: any) {
      showSnack(e.response?.data?.detail || 'Model change failed', 'error');
    } finally {
      setLoading(null);
    }
  };

  const cancelModelChange = async () => {
    if (!config?.pending_model_change) return;
    setLoading('model');
    try {
      await apiService.cancelAssistantModelChange();
      showSnack('Model change cancelled');
      await load();
    } catch (e: any) {
      showSnack(e.response?.data?.detail || 'Failed to cancel', 'error');
    } finally {
      setLoading(null);
    }
  };

  const border = isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)';

  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">Assistant configuration is available to super admins only.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', color: 'text.primary', letterSpacing: '-0.02em' }}>
            Assistant configuration
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mt: 0.25 }}>
            Configure the OpenAI key, caps, and model for the built-in AI assistant
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={load}
          disabled={loading !== null}
          startIcon={loading === 'load' ? <CircularProgress size={16} /> : <RefreshOutlined />}
          sx={{ textTransform: 'none' }}
        >
          Refresh
        </Button>
      </Box>

      {config && (
        <Box sx={{ display: 'flex', gap: 2.5, mb: 2.5, flexWrap: 'wrap' }}>
          <StateChip label="Configured" ok={config.configured} isDark={isDark} />
          <StateChip label="Active" ok={config.enabled} isDark={isDark} />
          {config.api_key_invalid && (
            <Chip icon={<ErrorOutlineOutlined />} label="OpenAI key invalid" color="error" size="small" />
          )}
          {config.openai_credits_exhausted && (
            <Chip icon={<ErrorOutlineOutlined />} label="OpenAI credits exhausted" color="error" size="small" />
          )}
          <Chip label={`Model: ${config.model ?? '—'}`} size="small" sx={{ fontWeight: 700 }} />
          <Chip label={config.masked_openai_key ?? 'No key set'} size="small" sx={{ fontFamily: 'monospace' }} />
        </Box>
      )}

      {config?.pending_model_change && (
        <Alert
          severity="warning"
          icon={<PendingActionsOutlined />}
          sx={{ mb: 2.5 }}
          action={
            <Button color="inherit" size="small" onClick={cancelModelChange} disabled={loading === 'model'}>
              Cancel
            </Button>
          }
        >
          Model change pending: {config.pending_model_change.current_model || '—'} → <b>{config.pending_model_change.new_model}</b>.
          {' '}
          {config.pending_model_change.approvals.length}/{config.pending_model_change.approvals_needed} approvals — a different super admin must confirm from the Requests page.
          The current model keeps serving until then.
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
        {/* ── OpenAI key ─────────────────────────────────────────── */}
        <Paper elevation={1} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
          <SectionHeader
            title="OpenAI API key"
            subtitle="Used by the assistant to make chat completions on OpenAI"
            icon={<KeyOutlined fontSize="small" />}
            color="#F59E0B"
            isDark={isDark}
            border={border}
          />
          <Box sx={{ px: 3, py: 2.5 }}>
            {config?.masked_openai_key && (
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mb: 1.5 }}>
                Current key: <Box component="span" sx={{ fontFamily: 'monospace' }}>{config.masked_openai_key}</Box>
              </Typography>
            )}
            <TextField
              label="New OpenAI API key"
              value={openAiKey}
              type="password"
              onChange={(e) => setOpenAiKey(e.target.value)}
              fullWidth
              size="small"
              placeholder="sk-..."
              helperText="Saving replaces the current key. Rejected if it doesn't start with 'sk-'."
            />
            <Button
              variant="contained"
              onClick={saveKey}
              disabled={loading !== null || !openAiKey.trim()}
              startIcon={loading === 'save-key' ? <CircularProgress size={16} color="inherit" /> : <SaveOutlined />}
              sx={{ mt: 2, textTransform: 'none' }}
            >
              Save key
            </Button>
          </Box>
        </Paper>

        {/* ── Model ─────────────────────────────────────────── */}
        <Paper elevation={1} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
          <SectionHeader
            title="Model"
            subtitle="Requires 2 super-admin approvals to change"
            icon={<ModelTrainingOutlined fontSize="small" />}
            color="#8B5CF6"
            isDark={isDark}
            border={border}
          />
          <Box sx={{ px: 3, py: 2.5 }}>
            <TextField
              label="OpenAI model id"
              value={nextModel}
              onChange={(e) => setNextModel(e.target.value)}
              fullWidth
              size="small"
              placeholder="gpt-4o-mini"
              disabled={!!config?.pending_model_change}
              helperText={
                config?.pending_model_change
                  ? 'A model change is already pending — cancel it above to switch targets.'
                  : `Current: ${config?.model ?? '—'}. Two super admins must agree before the change is applied.`
              }
            />
            <Button
              variant="contained"
              onClick={() => setModelDialogOpen(true)}
              disabled={loading !== null || !nextModel.trim() || nextModel.trim() === (config?.model ?? '') || !!config?.pending_model_change}
              startIcon={loading === 'model' ? <CircularProgress size={16} color="inherit" /> : <ModelTrainingOutlined />}
              sx={{ mt: 2, textTransform: 'none' }}
            >
              Request model change
            </Button>
          </Box>
        </Paper>

        {/* ── Caps ─────────────────────────────────────────── */}
        <Paper elevation={1} sx={{ borderRadius: '16px', overflow: 'hidden', gridColumn: { xs: 'auto', md: '1 / -1' } }}>
          <SectionHeader
            title="Token caps"
            subtitle="Same daily / weekly / monthly cap model as regular apps"
            icon={<TuneOutlined fontSize="small" />}
            color="#3B82F6"
            isDark={isDark}
            border={border}
          />
          <Box sx={{ px: 3, py: 2.5 }}>
            <Typography sx={miniHeader}>Daily</Typography>
            <Box sx={twoCol}>
              <TextField label="Daily input" type="number" size="small" fullWidth
                value={caps.token_limit_input}
                onChange={(e) => setCaps({ ...caps, token_limit_input: e.target.value })} />
              <TextField label="Daily output" type="number" size="small" fullWidth
                value={caps.token_limit_output}
                onChange={(e) => setCaps({ ...caps, token_limit_output: e.target.value })} />
            </Box>
            <Typography sx={{ ...miniHeader, mt: 2 }}>Weekly (optional)</Typography>
            <Box sx={twoCol}>
              <TextField label="Weekly input cap" type="number" size="small" fullWidth
                placeholder="No cap"
                value={caps.weekly_token_limit_input}
                onChange={(e) => setCaps({ ...caps, weekly_token_limit_input: e.target.value })} />
              <TextField label="Weekly output cap" type="number" size="small" fullWidth
                placeholder="No cap"
                value={caps.weekly_token_limit_output}
                onChange={(e) => setCaps({ ...caps, weekly_token_limit_output: e.target.value })} />
            </Box>
            <Typography sx={{ ...miniHeader, mt: 2 }}>Monthly (optional)</Typography>
            <Box sx={twoCol}>
              <TextField label="Monthly input cap" type="number" size="small" fullWidth
                placeholder="No cap"
                value={caps.monthly_token_limit_input}
                onChange={(e) => setCaps({ ...caps, monthly_token_limit_input: e.target.value })} />
              <TextField label="Monthly output cap" type="number" size="small" fullWidth
                placeholder="No cap"
                value={caps.monthly_token_limit_output}
                onChange={(e) => setCaps({ ...caps, monthly_token_limit_output: e.target.value })} />
            </Box>
            <Button
              variant="contained"
              onClick={saveLimits}
              disabled={loading !== null}
              startIcon={loading === 'save-limits' ? <CircularProgress size={16} color="inherit" /> : <SaveOutlined />}
              sx={{ mt: 2, textTransform: 'none' }}
            >
              Save limits
            </Button>
          </Box>
        </Paper>

        {/* ── Usage ─────────────────────────────────────────── */}
        <Paper elevation={1} sx={{ borderRadius: '16px', overflow: 'hidden', gridColumn: { xs: 'auto', md: '1 / -1' } }}>
          <SectionHeader
            title="Usage"
            subtitle="Live token consumption against caps"
            icon={<TuneOutlined fontSize="small" />}
            color="#10B981"
            isDark={isDark}
            border={border}
          />
          <Box sx={{ px: 3, py: 2.5, display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' } }}>
            <UsageCard
              label="Today"
              usage={config?.usage_today}
              inputCap={config?.token_limit_input}
              outputCap={config?.token_limit_output}
              isDark={isDark}
            />
            <UsageCard
              label="This week"
              usage={config?.usage_this_week}
              inputCap={config?.weekly_token_limit_input}
              outputCap={config?.weekly_token_limit_output}
              isDark={isDark}
            />
            <UsageCard
              label="This month"
              usage={config?.usage_this_month}
              inputCap={config?.monthly_token_limit_input}
              outputCap={config?.monthly_token_limit_output}
              isDark={isDark}
            />
          </Box>
          {config?.daily_history && config.daily_history.length > 0 && (
            <Box sx={{ px: 3, py: 2, borderTop: border }}>
              <Typography sx={miniHeader}>Last {config.daily_history.length} days (input + output)</Typography>
              <DailyBars history={config.daily_history} isDark={isDark} />
            </Box>
          )}
        </Paper>
      </Box>

      <Dialog open={modelDialogOpen} onClose={() => setModelDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Request model change</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 2 }}>
            You are proposing to switch the assistant from
            {' '}<Box component="span" sx={{ fontFamily: 'monospace' }}>{config?.model || '—'}</Box>{' '}
            to <Box component="span" sx={{ fontFamily: 'monospace' }}>{nextModel}</Box>.
            A different super admin must confirm this change from the Requests page before it takes effect.
          </Typography>
          <TextField
            label="Note (optional)"
            value={modelNote}
            onChange={(e) => setModelNote(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModelDialogOpen(false)}>Cancel</Button>
          <Button onClick={submitModelChange} variant="contained" disabled={loading === 'model'}>
            {loading === 'model' ? 'Submitting…' : 'Submit request'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} variant="filled">{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

const miniHeader = {
  fontSize: '0.7rem',
  color: 'text.secondary',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  mb: 1,
} as const;

const twoCol = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 };

const SectionHeader: React.FC<{
  title: string; subtitle: string; icon: React.ReactNode; color: string; isDark: boolean; border: string;
}> = ({ title, subtitle, icon, color, isDark, border }) => (
  <Box sx={{ px: 3, py: 2, borderBottom: border, display: 'flex', alignItems: 'center', gap: 1.5 }}>
    <Box sx={{
      width: 36, height: 36, borderRadius: '10px',
      bgcolor: isDark ? alpha(color, 0.12) : alpha(color, 0.1),
      border: `1px solid ${alpha(color, isDark ? 0.25 : 0.2)}`,
      color, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>{icon}</Box>
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary' }}>{title}</Typography>
      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{subtitle}</Typography>
    </Box>
  </Box>
);

const StateChip: React.FC<{ label: string; ok: boolean; isDark: boolean }> = ({ label, ok, isDark }) => (
  <Chip
    size="small"
    icon={ok ? <CheckCircleOutlined /> : <ErrorOutlineOutlined />}
    label={`${label}: ${ok ? 'Yes' : 'No'}`}
    sx={{
      bgcolor: ok
        ? (isDark ? alpha('#10B981', 0.15) : '#DCFCE7')
        : (isDark ? alpha('#EF4444', 0.15) : '#FEE2E2'),
      color: ok ? '#059669' : '#DC2626',
      fontWeight: 700,
    }}
  />
);

const UsageCard: React.FC<{
  label: string;
  usage?: AssistantUsageSummary;
  inputCap?: number | null;
  outputCap?: number | null;
  isDark: boolean;
}> = ({ label, usage, inputCap, outputCap, isDark }) => {
  const inputPct = inputCap && usage ? Math.min(100, Math.round((usage.input_tokens / inputCap) * 100)) : null;
  const outputPct = outputCap && usage ? Math.min(100, Math.round((usage.output_tokens / outputCap) * 100)) : null;
  return (
    <Box sx={{
      p: 2.5, borderRadius: '12px',
      bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC',
      border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid #E2E8F0',
    }}>
      <Typography sx={{ ...miniHeader, mb: 1 }}>{label}</Typography>
      <Row lbl="Input" value={usage?.input_tokens ?? 0} cap={inputCap ?? null} pct={inputPct} />
      <Row lbl="Output" value={usage?.output_tokens ?? 0} cap={outputCap ?? null} pct={outputPct} />
      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 1 }}>
        {(usage?.request_count ?? 0).toLocaleString()} requests
      </Typography>
    </Box>
  );
};

const Row: React.FC<{ lbl: string; value: number; cap: number | null; pct: number | null }> = ({ lbl, value, cap, pct }) => (
  <Box sx={{ mb: 1.25 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{lbl}</Typography>
      <Typography sx={{ fontSize: '0.78rem', color: 'text.primary', fontFamily: 'monospace' }}>
        {value.toLocaleString()}{cap ? ` / ${cap.toLocaleString()}` : ''}
      </Typography>
    </Box>
    {pct !== null && (
      <Tooltip title={`${pct}%`}>
        <LinearProgress
          variant="determinate"
          value={pct}
          color={pct >= 90 ? 'error' : pct >= 75 ? 'warning' : 'primary'}
          sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
        />
      </Tooltip>
    )}
  </Box>
);

const DailyBars: React.FC<{
  history: Array<{ date: string; input_tokens: number; output_tokens: number }>;
  isDark: boolean;
}> = ({ history, isDark }) => {
  const max = useMemo(
    () => Math.max(1, ...history.map(d => (d.input_tokens || 0) + (d.output_tokens || 0))),
    [history],
  );
  return (
    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-end', mt: 1, height: 80 }}>
      {history.map(d => {
        const total = (d.input_tokens || 0) + (d.output_tokens || 0);
        const pct = (total / max) * 100;
        return (
          <Tooltip key={d.date} title={`${d.date} (UTC): ${total.toLocaleString()} tokens`}>
            <Box sx={{
              flex: 1,
              height: `${Math.max(2, pct)}%`,
              bgcolor: isDark ? '#3B82F6' : '#60A5FA',
              borderRadius: '3px 3px 0 0',
              transition: 'opacity 0.15s',
              '&:hover': { opacity: 0.8 },
            }} />
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default AssistantConfigPage;
